/**
 * Agent participant detection — spec §7.
 *
 * Production (agents-cdn) uses two rules in different places:
 *  - RPC destination / text-stream handlers: `identity?.includes("agent")`
 *  - `useVoiceAssistant` (transcription/audio): `kind === ParticipantKind.AGENT`
 *
 * The adapter uses the combined rule recommended by the spec so both paths
 * resolve the same participant: kind first, identity substring as fallback.
 */

import { ParticipantKind, type Participant, type RemoteParticipant, type Room } from "livekit-client";

/** Production's substring rule (App.tsx text-stream handlers). */
export function isAgentIdentity(identity: string | undefined): boolean {
  return identity?.includes("agent") ?? false;
}

/** Combined rule: participant kind wins, identity substring as fallback. */
export function isAgentParticipant(participant: Participant): boolean {
  return participant.kind === ParticipantKind.AGENT || isAgentIdentity(participant.identity);
}

/**
 * Resolve the agent among the room's remote participants. Kind-tagged agents
 * take priority over substring matches so a human named "agent-tester" never
 * shadows a real agent.
 */
export function findAgentParticipant(room: Room): RemoteParticipant | undefined {
  const remotes = Array.from(room.remoteParticipants.values());
  return remotes.find((p) => p.kind === ParticipantKind.AGENT) ?? remotes.find((p) => isAgentIdentity(p.identity));
}
